/**
 * Shapes and shared reading rules for the admin transactions screens.
 *
 * A "transaction" here is a `Location::PaymentAttempt` — the record that carries
 * the money, the diner, the bill and the service fee. The Stripe-side
 * `Location::PaymentTransaction` hangs off it as `provider`, and the two stay
 * separate on both screens: an attempt with no provider row never reached Stripe
 * at all, which is a different failure from one Stripe refused.
 */

export type TransactionRow = {
  id: number
  status: string
  statusLabel: string
  paymentMethod: string
  paymentMethodLabel: string
  totalAmount: string
  serviceFeeAmount: string
  createdAt: string
  /** Truncated on the index; the detail screen carries all of it. */
  failureReason: string | null
  location: { id: number; name: string }
  diner: { id: number; name: string; email: string }
  billId: number
  /** Null when the attempt never reached the provider. */
  providerStatus: string | null
}

export type TransactionProvider = {
  id: number
  provider: string
  status: string
  statusLabel: string
  providerStatus: string | null
  currency: string
  amountTotalCents: number
  applicationFeeAmountCents: number
  connectedAccountId: string
  checkoutSessionId: string | null
  paymentIntentId: string | null
  chargeId: string | null
  refundId: string | null
  receiptUrl: string | null
  /**
   * The field the screen exists for. `CheckoutSessionService` rescues the
   * provider's error and answers its caller with a Result, so before this screen
   * the only way to read it was a database console.
   */
  lastProviderError: string | null
  checkoutExpiresAt: string | null
  completedAt: string | null
  createdAt: string
}

/** Which of the three shapes an attempt takes. They are read differently. */
export type TransactionKind = "orders" | "share" | "pickup"

export type TransactionDetail = TransactionRow & {
  kind: TransactionKind
  idempotencyKey: string
  selectedOrderIds: number[]
  selectedSubtotal: string
  tipAmount: string
  serviceFeePercentage: string
  completedAt: string | null
  billSplitId: number | null
  bill: { id: number; status: string; statusLabel: string } | null
  snapshot: Record<string, unknown>
  provider: TransactionProvider | null
}

/**
 * Status colour, and only in the badge. Per the design system the figures stay
 * `text-foreground` — colour lives in small marks, not in large text.
 */
export function attemptStatusVariant(status: string) {
  if (status === "failed" || status === "canceled") return "destructive"
  if (status === "succeeded") return "outline"

  return "secondary"
}
